/* tslint:disable:variable-name*/
import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Flex, Box, Text, Input } from '@mafc/trolly';
import COLORS from 'theme/colors';
import TYPOGRAPHY from 'theme/typography';
import NewCardFormStyled from '../../styles/NewCardForm.styles';
import {
  isValidCreditCard,
  validateExpiryDate,
  getCardType,
  validateCVV,
  validateCardHolderName,
  extractBinFromCard,
} from '../../helpers/payment.helpers';
import useAction from 'hooks/useAction';
import { setPaymentMode, storePaymentDetails } from 'pages/Checkout/redux/actions/payment.action';
import EPaymentMethods from '../../PamentMethods.enum';
import { isExisty } from 'utils/helpers.util';
import { ReactComponent as IconCVV } from 'assets/svgs/cvv.svg';
import { isRTL } from 'config/localisation';
import { VoucherType } from 'network/response/ICart';
import usePayment from '../../hooks/usePayment';
import FEATURES_CONFIG from 'config/features';
import CardIcons from '../CardIcons/CardIcons';

const { Caption, Paragraph } = Text;

export const CREDIT_CARD_MASKS = {
  cardNumber_en: '9999 9999 9999 9999',
  cardNumber_ar: '9999999999999999',
  cardExpiry: '99/99',
  cardCVV: '9999',
  cardHolderName: '',
};
// will be used in the future when we have to save the cards
// export const SwitchLayout = customStyled(Switch)`
//   input + div:before {
//     width: 32px;
//     height: 22px;
//     border-radius: 12px;
//     opacity: 1;
//   }
//   input + div:after {
//     width: 22px;
//     height: 22px;
//     top: 48%;
//     box-shadow: none;
//     border 1px solid rgba(0,0,0,.2);
//     background: white;
//   }
//   input:checked + div:after {
//     transform: translate(50%, -50%);
//   }

// `;

const INITIAL_STATE = {
  card_number: '',
  card_expiry_date: '',
  card_cvn: '',
  card_type: '',
  card_holder_name: '',
};

interface Props {
  saveCard: boolean;
  paymentDetails: any;
  disabled: boolean;
}

const NewCardForm = ({ paymentDetails, saveCard, disabled }: Props) => {
  const { errors, setValue, setError, clearErrors } = useForm<any>({
    mode: 'onBlur',
  });
  const [cardDetails, setCardDetails] = useState({ ...INITIAL_STATE, ...paymentDetails });
  const [savePaymentInfo] = useState(isExisty(saveCard) ? saveCard : true);
  const [storePaymentDetailsAction] = useAction(storePaymentDetails);
  const [setPaymentAction] = useAction(setPaymentMode);
  const { appliedVoucher } = usePayment();

  // Translate for multilingual support & rtl check helper
  const rtl = isRTL();
  const { t } = useTranslation();

  // pre fill the values from paymentDetails prop
  useEffect(() => {
    if (Object.keys(paymentDetails).length > 0) {
      Object.keys(paymentDetails).forEach((item: string) => {
        if (paymentDetails[item]) {
          setValue(item, paymentDetails[item], { shouldValidate: true });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVariant = (key: string) => {
    const fieldTouched = cardDetails[`${key}-touched`];
    return errors[key] ? 'error' : fieldTouched ? 'success' : 'primary';
  };
  // will be used in future
  // const toggleSaveCard = ({ target: { checked } }: any) => {
  //   setSavePaymentInfo(checked);
  //   storeCardDetails(checked);
  // };

  const formChange = () => {
    storeCardDetails();
  };

  const isValidForm = (values: any) => {
    // TODO enhance this implementation
    return (
      isValidCreditCard(values.card_number) &&
      validateExpiryDate(values.card_expiry_date) &&
      validateCVV(values.card_cvn) &&
      validateCardHolderName(values.card_holder_name)
    );
  };

  const storeCardDetails = useCallback(
    (saveInfo?: boolean) => {
      const card_type = getCardType(cardDetails.card_number);

      storePaymentDetailsAction(EPaymentMethods.CREDIT_CARD, {
        newCardDetails: { ...cardDetails, card_type },
        saved: false,
        savePaymentInfo: isExisty(saveInfo) ? saveInfo : savePaymentInfo,
        isValid: isValidForm(cardDetails),
      });
    },
    [cardDetails, savePaymentInfo, storePaymentDetailsAction]
  );
  const { disableBinCode = false } = FEATURES_CONFIG.settings;

  const validateForError = (
    e: any,
    field: string,
    message: string,
    validate: any,
    isBlurred: boolean = false
  ) => {
    const fieldTouched = isBlurred || cardDetails[`${field}-touched`];
    if (fieldTouched && !validate(e.target.value)) {
      setError(field, { type: 'custom', message });
    } else {
      clearErrors(field);
    }
  };

  const handleFieldsChange = (field: string, validate: any, message: string) => (e: any) => {
    setValue(field, e.target.value, { shouldValidate: false });
    validateForError(e, field, message, validate, false);
    setCardDetails({
      ...cardDetails,
      [field]: e.target.value,
    });
  };

  const onBlurForm = (field: any, validate: any, message: string) => (e: any) => {
    validateForError(e, field, message, validate, true);
    setCardDetails({
      ...cardDetails,
      [`${field}-touched`]: true,
    });
    if (field === 'card_number' && !disableBinCode) {
      const cardNumber = e.target.value;
      const card_type = getCardType(cardDetails.card_number);
      const paymentDetailsState = {
        newCardDetails: { ...cardDetails, card_type },
        saved: false,
        savePaymentInfo,
        isValid: isValidForm(cardDetails),
      };
      // TODO: Remove the setimeout later, adding as hotfix
      if (cardNumber && isValidCreditCard(cardNumber)) {
        const binCode = extractBinFromCard(cardNumber);
        setTimeout(() => {
          setPaymentAction('creditcard', paymentDetailsState, binCode, false);
        });
      }
      if (appliedVoucher && cardNumber === '') {
        setTimeout(() => {
          setPaymentAction('creditcard', paymentDetailsState, '', false);
        });
      }
    }
  };

  // Validate Place Order CTA via formChange, whenever,
  // new instance of card Details is returned i.e. new card details
  useEffect(formChange, [cardDetails, storeCardDetails]);

  return (
    <NewCardFormStyled>
      <Box>
        <form autoComplete="off">
          <Box height="65px">
            <Input
              label={t('paymentForm.cardNumber')}
              name="card_number"
              variant={getVariant('card_number')}
              mask={rtl ? CREDIT_CARD_MASKS.cardNumber_ar : CREDIT_CARD_MASKS.cardNumber_en}
              maskChar={null}
              mui
              iconRight={<CardIcons cardNumber={cardDetails.card_number} />}
              onChange={handleFieldsChange(
                'card_number',
                isValidCreditCard,
                t('paymentForm.invalidCardNumber')
              )}
              onBlur={onBlurForm(
                'card_number',
                isValidCreditCard,
                t('paymentForm.invalidCardNumber')
              )}
              data-testid="__checkout_payments_card_number__"
              autoComplete="cc-number"
              disabled={disabled}
            />
            {errors.card_number && (
              <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                {errors.card_number.message}
              </Paragraph>
            )}
            {!errors.card_number && appliedVoucher && appliedVoucher.type === VoucherType.bin && (
              <Paragraph color={COLORS.GREEN} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                {appliedVoucher.description}
              </Paragraph>
            )}
          </Box>
          <Box width={1} height="65px">
            <Input
              label={t('paymentForm.cardHolderName')}
              name="card_holder_name"
              variant={getVariant('card_holder_name')}
              mask={CREDIT_CARD_MASKS.cardHolderName}
              onChange={handleFieldsChange(
                'card_holder_name',
                validateCardHolderName,
                t('paymentForm.invalidCardHolderName')
              )}
              onBlur={onBlurForm(
                'card_holder_name',
                validateCardHolderName,
                t('paymentForm.invalidCardHolderName')
              )}
              data-testid="__checkout_payments_card_holder_name__"
              autoComplete="cc-name"
              disabled={disabled}
              mui
            />
            {errors.card_holder_name && (
              <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                {errors.card_holder_name.message}
              </Paragraph>
            )}
          </Box>
          <Flex justifyContent="space-between" flexWrap="wrap" mb={3}>
            <Box width={[1 / 2]} pr={3} height="65px">
              <Input
                label={t('paymentForm.expiryDate')}
                mask={CREDIT_CARD_MASKS.cardExpiry}
                variant={getVariant('card_expiry_date')}
                name="card_expiry_date"
                onChange={handleFieldsChange(
                  'card_expiry_date',
                  validateExpiryDate,
                  t('paymentForm.invalidExpiryDate')
                )}
                onBlur={onBlurForm(
                  'card_expiry_date',
                  validateExpiryDate,
                  t('paymentForm.invalidExpiryDate')
                )}
                data-testid="__checkout_payments_card_expiry__"
                autoComplete="cc-exp"
                disabled={disabled}
                mui
              />
              {errors.card_expiry_date && (
                <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                  {errors.card_expiry_date.message}
                </Paragraph>
              )}
            </Box>
            <Box width={[1 / 2]} height="65px">
              <Input
                label={t('paymentForm.cvv')}
                mask={CREDIT_CARD_MASKS.cardCVV}
                variant={getVariant('card_cvn')}
                onChange={handleFieldsChange('card_cvn', validateCVV, t('paymentForm.invalidCvv'))}
                onBlur={onBlurForm('card_cvn', validateCVV, t('paymentForm.invalidCvv'))}
                name="card_cvn"
                iconRight={<IconCVV />}
                data-testid="__checkout_payments_card_cvv__"
                autoComplete="cc-cvn"
                disabled={disabled}
                mui
              />
              {errors.card_cvn && (
                <Paragraph color={COLORS.RED} mt={1} fontSize={TYPOGRAPHY.SIZE[10]}>
                  {errors.card_cvn.message}
                </Paragraph>
              )}
            </Box>
          </Flex>
        </form>
        {/* NOTE: THIS WILL BE USED In FUTURE WHEN we have to save card */}
        {false && (
          <Flex flexDirection={['column', 'row']} justifyContent="flex-end">
            <Flex
              my={3}
              justifyContent="space-between"
              flexDirection={['row', 'row-reverse']}
              alignItems="center"
            >
              <Caption color={COLORS.THUNDORA} fontSize={2} fontWeight={5} ml={[0, 0, 2]}>
                {t('paymentForm.save')}
              </Caption>
              {/* NOTE: THIS WILL BE USED In FUTURE WHEN we have to save card */}
              {/* <SwitchLayout onChange={toggleSaveCard} checked={savePaymentInfo} /> */}
            </Flex>
          </Flex>
        )}
      </Box>
    </NewCardFormStyled>
  );
};

export default NewCardForm;
